/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/pages/IntelTelemetry.tsx
 * DESCRIPTION: Live telemetry dashboard for real-time intelligence on
 *              usage, user behavior, trends by regions globally.
 *              Admin-only access with Clerk metadata authentication.
 *              UPDATED: Fixed map, nested tabs, custom scrollbars,
 *                       no emojis, light mode dial fixes, removed unused imports
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 1.2.1
 * @created 2026-03-27
 * @updated 2026-03-27 - Fixed map visualization, added nested tabs,
 *                       custom scrollbars, removed emojis, fixed light mode
 * @updated 2026-04-03 - Removed unused imports, added Lock icon, prefixed
 *                       unused variables with underscore for TS compliance
 * ─────────────────────────────────────────────────────────────────────
 */
import { useState, useEffect } from 'react';
import { useUser } from '@clerk/react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import {
  Activity,
  Globe,
  TrendingUp,
  AlertTriangle,
  RefreshCw,
  Users,
  Clock,
  Server,
  CheckCircle,
  Tag,
  Zap,
  Shield,
  LogOut,
  Palette,
  Video,
  Film,
  Wand2,
  Box,
  BarChart3,
  PieChart as PieChartIcon,
  Upload,
  Lock, // Added - was missing, causing JSX error
  Sparkles,
} from 'lucide-react';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
// ─────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────
interface TelemetryStats {
  activeUsers: number;
  requestsPerMin: number;
  avgProcessingTime: number;
  riskAlerts: number;
}
interface GeoData {
  country: string;
  uploads: number;
  riskLevel: 'low' | 'medium' | 'high';
  avgTrustScore: number;
  coordinates: { lat: number; lng: number };
}
interface CreatorTool {
  rank: number;
  tool: string;
  uploadCount: number;
  avgTrust: number;
  riskPercent: number;
  trend: number[];
  icon?: React.ElementType;
}
interface ContentType {
  name: string;
  value: number;
  color: string;
}
interface ActivityEvent {
  id: string;
  timestamp: string;
  type: 'upload' | 'alert' | 'tag' | 'milestone';
  description: string;
  riskLevel?: 'low' | 'medium' | 'high';
  countryCode?: string;
}
interface TagAdoption {
  tag: string;
  count: number;
  growthRate: number;
  suggestedCategory: string;
}
interface ProviderStatus {
  name: string;
  responseTime: number;
  successRate: number;
  errors: number;
  status: 'operational' | 'degraded' | 'down';
}
type TimeRange = '5m' | '15m' | '1h' | '24h';
type RegionFilter = 'global' | 'region';
type ActiveTab = 'map' | 'content' | 'behavior' | 'system';
type ContentSubTab = 'tools' | 'distribution';
type BehaviorSubTab = 'patterns' | 'tags';
// ─────────────────────────────────────────────────────────────────────
// MOCK DATA
// ─────────────────────────────────────────────────────────────────────
const MOCK_STATS: TelemetryStats = {
  activeUsers: 1247,
  requestsPerMin: 342,
  avgProcessingTime: 2.3,
  riskAlerts: 23,
};
const MOCK_GEO_DATA: GeoData[] = [
  { country: 'United States', uploads: 450, riskLevel: 'low', avgTrustScore: 0.92, coordinates: { lat: 37.0902, lng: -95.7129 } },
  { country: 'United Kingdom', uploads: 280, riskLevel: 'low', avgTrustScore: 0.89, coordinates: { lat: 55.3781, lng: -3.4360 } },
  { country: 'Germany', uploads: 220, riskLevel: 'low', avgTrustScore: 0.91, coordinates: { lat: 51.1657, lng: 10.4515 } },
  { country: 'China', uploads: 380, riskLevel: 'medium', avgTrustScore: 0.78, coordinates: { lat: 35.8617, lng: 104.1954 } },
  { country: 'India', uploads: 320, riskLevel: 'medium', avgTrustScore: 0.82, coordinates: { lat: 20.5937, lng: 78.9629 } },
  { country: 'Brazil', uploads: 180, riskLevel: 'low', avgTrustScore: 0.87, coordinates: { lat: -14.2350, lng: -51.9253 } },
  { country: 'Australia', uploads: 150, riskLevel: 'low', avgTrustScore: 0.93, coordinates: { lat: -25.2744, lng: 133.7751 } },
  { country: 'Japan', uploads: 260, riskLevel: 'low', avgTrustScore: 0.94, coordinates: { lat: 36.2048, lng: 138.2529 } },
  { country: 'Russia', uploads: 190, riskLevel: 'high', avgTrustScore: 0.65, coordinates: { lat: 61.5240, lng: 105.3188 } },
  { country: 'South Africa', uploads: 95, riskLevel: 'medium', avgTrustScore: 0.79, coordinates: { lat: -30.5595, lng: 22.9375 } },
];
const MOCK_CREATOR_TOOLS: CreatorTool[] = [
  { rank: 1, tool: 'Midjourney', uploadCount: 3300, avgTrust: 0.92, riskPercent: 15, trend: [65, 72, 68, 75, 82, 79, 85], icon: Sparkles },
  { rank: 2, tool: 'Photoshop', uploadCount: 474, avgTrust: 0.95, riskPercent: 26, trend: [45, 48, 52, 49, 55, 58, 62], icon: Palette },
  { rank: 3, tool: 'FFmpeg', uploadCount: 358, avgTrust: 0.92, riskPercent: 13, trend: [32, 35, 38, 36, 40, 42, 45], icon: Video },
  { rank: 4, tool: 'Final Cut', uploadCount: 339, avgTrust: 0.92, riskPercent: 19, trend: [28, 30, 33, 31, 35, 37, 40], icon: Film },
  { rank: 5, tool: 'Runway', uploadCount: 163, avgTrust: 0.86, riskPercent: 15, trend: [15, 18, 20, 19, 22, 24, 26], icon: Wand2 },
  { rank: 6, tool: 'DALL-E', uploadCount: 137, avgTrust: 0.95, riskPercent: 19, trend: [12, 14, 16, 15, 18, 20, 22], icon: Sparkles },
  { rank: 7, tool: 'Stable Diffusion', uploadCount: 298, avgTrust: 0.88, riskPercent: 22, trend: [25, 28, 32, 30, 35, 38, 42], icon: Box },
  { rank: 8, tool: 'Unknown/Other', uploadCount: 378, avgTrust: 0.92, riskPercent: 100, trend: [35, 38, 40, 39, 42, 44, 47], icon: Box },
];
const MOCK_CONTENT_TYPES: ContentType[] = [
  { name: 'Sports', value: 18, color: '#3b82f6' },
  { name: 'Music', value: 15, color: '#8b5cf6' },
  { name: 'Food', value: 12, color: '#f59e0b' },
  { name: 'Travel', value: 10, color: '#10b981' },
  { name: 'Political', value: 8, color: '#ef4444' },
  { name: 'Kids', value: 7, color: '#ec4899' },
  { name: 'Adult', value: 5, color: '#6b7280' },
  { name: 'Movie Shorts', value: 9, color: '#06b6d4' },
  { name: 'News', value: 6, color: '#84cc16' },
  { name: 'Education', value: 6, color: '#14b8a6' },
  { name: 'Other', value: 4, color: '#64748b' },
];
const MOCK_ACTIVITY_FEED: ActivityEvent[] = [
  { id: '1', timestamp: '12:34:56', type: 'upload', description: 'User uploaded video.mp4 (Trust: 0.92)', countryCode: 'US' },
  { id: '2', timestamp: '12:34:56', type: 'upload', description: 'User uploaded video.mp4 (Trust: 0.92)', countryCode: 'US' },
  { id: '3', timestamp: '12:34:58', type: 'alert', description: 'Risk alert: Inappropriate content detected', riskLevel: 'high', countryCode: 'CN' },
  { id: '4', timestamp: '12:35:01', type: 'tag', description: 'New tag suggested: #animeart → Category: Entertainment', countryCode: 'JP' },
  { id: '5', timestamp: '12:35:03', type: 'milestone', description: 'Global milestone: 10,000th analysis completed', countryCode: 'GLOBAL' },
  { id: '6', timestamp: '12:35:15', type: 'upload', description: 'User uploaded image.png (Trust: 0.88)', countryCode: 'GB' },
  { id: '7', timestamp: '12:35:22', type: 'alert', description: 'Risk alert: Manipulation detected in media file', riskLevel: 'medium', countryCode: 'DE' },
  { id: '8', timestamp: '12:35:30', type: 'upload', description: 'User uploaded audio.mp3 (Trust: 0.95)', countryCode: 'DE' },
];
const MOCK_TAG_ADOPTION: TagAdoption[] = [
  { tag: '#aiart', count: 48, growthRate: 4.8, suggestedCategory: 'Entertainment' },
  { tag: '#travelvlog', count: 77, growthRate: 1.2, suggestedCategory: 'Travel' },
  { tag: '#politicalmemes', count: 26, growthRate: -1.5, suggestedCategory: 'Political' },
  { tag: '#cooking', count: 29, growthRate: 2.3, suggestedCategory: 'Food' },
  { tag: '#travelblog', count: 24, growthRate: 1.8, suggestedCategory: 'Travel' },
  { tag: '#coalint', count: 10, growthRate: 0.5, suggestedCategory: 'Politics' },
  { tag: '#foodie', count: 7, growthRate: 1.1, suggestedCategory: 'Food' },
  { tag: '#digitalart', count: 1, growthRate: 0.2, suggestedCategory: 'Entertainment' },
];
const MOCK_PROVIDER_STATUS: ProviderStatus[] = [
  { name: 'Mistral', responseTime: 89, successRate: 99, errors: 0, status: 'operational' },
  { name: 'Qwen', responseTime: 75, successRate: 98, errors: 1, status: 'operational' },
  { name: 'OpenAI', responseTime: 75, successRate: 97, errors: 0, status: 'operational' },
];
const MOCK_UPLOAD_PATTERNS = [
  { hour: 0, weekday: 25, weekend: 15 },
  { hour: 4, weekday: 18, weekend: 12 },
  { hour: 8, weekday: 45, weekend: 35 },
  { hour: 12, weekday: 52, weekend: 48 },
  { hour: 16, weekday: 48, weekend: 52 },
  { hour: 20, weekday: 35, weekend: 42 },
  { hour: 24, weekday: 28, weekend: 22 },
];
const MOCK_CATEGORY_TREND = [
  { time: '00:00', value: 120 },
  { time: '04:00', value: 95 },
  { time: '08:00', value: 180 },
  { time: '12:00', value: 250 },
  { time: '16:00', value: 220 },
  { time: '20:00', value: 190 },
  { time: '24:00', value: 150 },
];
const MOCK_QUEUE_DEPTH = [
  { time: '11:00', depth: 8 },
  { time: '12:00', depth: 12 },
  { time: '13:00', depth: 15 },
  { time: '14:00', depth: 10 },
  { time: '15:00', depth: 13 },
  { time: '16:00', depth: 11 },
];
const MOCK_ERROR_RATE = [
  { time: '11:00', rate: 1.2 },
  { time: '12:00', rate: 2.5 },
  { time: '13:00', rate: 1.8 },
  { time: '14:00', rate: 1.5 },
  { time: '15:00', rate: 2.1 },
  { time: '16:00', rate: 1.6 },
];
// ─────────────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS
// ─────────────────────────────────────────────────────────────────────
const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};
const getRiskColor = (level: string) => {
  switch (level) {
    case 'low': return 'text-emerald-600 dark:text-emerald-400';
    case 'medium': return 'text-yellow-600 dark:text-yellow-400';
    case 'high': return 'text-red-600 dark:text-red-400';
    default: return 'text-gray-400';
  }
};
const getRiskBgColor = (level: string) => {
  switch (level) {
    case 'low': return 'bg-emerald-500';
    case 'medium': return 'bg-yellow-500';
    case 'high': return 'bg-red-500';
    default: return 'bg-gray-500';
  }
};
// Prefixed with underscore - declared but not currently used
// Can be enabled when provider status UI is expanded
const _getStatusColor = (status: string) => {
  switch (status) {
    case 'operational': return 'text-emerald-600 dark:text-emerald-400';
    case 'degraded': return 'text-yellow-600 dark:text-yellow-400';
    case 'down': return 'text-red-600 dark:text-red-400';
    default: return 'text-gray-400';
  }
};
const getCountryFlag = (code: string) => {
  const flags: Record<string, string> = {
    US: '🇺🇸',
    GB: '🇬🇧',
    DE: '🇩🇪',
    JP: '🇯🇵',
    CN: '🇨🇳',
    IN: '🇮🇳',
    GLOBAL: '🌍',
  };
  return flags[code] || '🌐';
};
// ─────────────────────────────────────────────────────────────────────
// GAUGE COMPONENT - Fixed for Light/Dark Mode
// ─────────────────────────────────────────────────────────────────────
interface GaugeProps {
  value: number;
  max?: number;
  label: string;
  sublabel?: string;
  color?: string;
  size?: 'sm' | 'md' | 'lg';
}
const Gauge: React.FC<GaugeProps> = ({
  value,
  max = 100,
  label,
  sublabel,
  color = '#10b981',
  size = 'md'
}) => {
  const percentage = Math.min((value / max) * 100, 100);
  const radius = size === 'sm' ? 30 : size === 'md' ? 40 : 50;
  const strokeWidth = size === 'sm' ? 6 : size === 'md' ? 8 : 10;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const dimensions = {
    sm: { width: 80, height: 80 },
    md: { width: 100, height: 100 },
    lg: { width: 120, height: 120 },
  };
  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={dimensions[size]}>
        <svg className="transform -rotate-90" width={dimensions[size].width} height={dimensions[size].height}>
          {/* Background circle */}
          <circle
            cx={dimensions[size].width / 2}
            cy={dimensions[size].height / 2}
            r={radius}
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
            fill="none"
          />
          {/* Progress circle */}
          <circle
            cx={dimensions[size].width / 2}
            cy={dimensions[size].height / 2}
            r={radius}
            stroke={color}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-500"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-${size === 'sm' ? 'lg' : size === 'md' ? 'xl' : '2xl'} font-bold text-gray-900 dark:text-white`}>
            {Math.round(percentage)}%
          </span>
        </div>
      </div>
      <p className="text-sm font-medium text-gray-900 dark:text-white mt-2">{label}</p>
      {sublabel && <p className="text-xs text-gray-600 dark:text-gray-400">{sublabel}</p>}
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────
const IntelTelemetry: React.FC = () => {
  // ───────────────────────────────────────────────────────────────────
  // STATE
  // ───────────────────────────────────────────────────────────────────
  const { user, isLoaded } = useUser();
  // Prefixed with underscore - declared but not currently used
  const { isAdminAuthenticated: _isAdminAuthenticated } = useAdminAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasValidLicense, setHasValidLicense] = useState(false);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // Tab state
  const [activeTab, setActiveTab] = useState<ActiveTab>('map');
  const [contentSubTab, setContentSubTab] = useState<ContentSubTab>('tools');
  const [behaviorSubTab, setBehaviorSubTab] = useState<BehaviorSubTab>('patterns');
  // Map controls
  const [regionFilter, setRegionFilter] = useState<RegionFilter>('global');
  const [timeRange, setTimeRange] = useState<TimeRange>('5m');
  // Prefixed with underscore - declared but not currently used
  const [isLive, _setIsLive] = useState(true);
  // Stats state (for real-time updates)
  const [stats, setStats] = useState<TelemetryStats>(MOCK_STATS);
  // Prefixed with underscore - declared but not currently used
  const [geoData, _setGeoData] = useState<GeoData[]>(MOCK_GEO_DATA);
  const [activityFeed, setActivityFeed] = useState<ActivityEvent[]>(MOCK_ACTIVITY_FEED);
  // Sort states
  const [sortField, setSortField] = useState<string>('rank');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  // Filters
  const [eventFilter, setEventFilter] = useState<string>('all');
  const [riskFilter, setRiskFilter] = useState<string>('all');
  const [feedEnabled, setFeedEnabled] = useState(true);
  // ───────────────────────────────────────────────────────────────────
  // EFFECTS
  // ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isLoaded) {
      checkAdminAccess();
    }
  }, [isLoaded, user]);
  useEffect(() => {
    if (isAdmin && hasValidLicense) {
      setIsLoading(false);
      // Simulate real-time updates
      const interval = setInterval(() => {
        if (isLive && feedEnabled) {
          updateStats();
        }
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isAdmin, hasValidLicense, isLive, feedEnabled]);
  // ───────────────────────────────────────────────────────────────────
  // HANDLERS
  // ───────────────────────────────────────────────────────────────────
  const checkAdminAccess = () => {
    // PERMANENT: Check Clerk metadata for admin role
    const userMetadata = user?.publicMetadata as {
      role?: string;
      license?: string;
    } | undefined;
    // Check for admin role OR enterprise license
    if (userMetadata?.role === 'admin') {
      setIsAdmin(true);
      setHasValidLicense(true);
      //console.log('Admin access granted via Clerk metadata role'); - do not enable this in live prod
      return;
    }
    // Check for business/enterprise license
    const licenseTier = userMetadata?.license || 'free';
    if (licenseTier === 'business' || licenseTier === 'enterprise') {
      setIsAdmin(true);
      setHasValidLicense(true);
     // console.log('Admin access granted via Clerk license tier');  - do not enable this in live prod
      return;
    }
    // No admin access
    // console.log('access denied');      - do not enable this in live prod
    setShowLicenseModal(true);
  };
  const updateStats = () => {
    // Simulate real-time data updates
    setStats(prev => ({
      activeUsers: prev.activeUsers + Math.floor(Math.random() * 10) - 5,
      requestsPerMin: prev.requestsPerMin + Math.floor(Math.random() * 20) - 10,
      avgProcessingTime: Math.max(1.5, prev.avgProcessingTime + (Math.random() * 0.4 - 0.2)),
      riskAlerts: prev.riskAlerts + (Math.random() > 0.7 ? 1 : 0),
    }));
    // Add new activity event occasionally
    if (Math.random() > 0.7) {
      const newEvent: ActivityEvent = {
        id: Date.now().toString(),
        timestamp: new Date().toLocaleTimeString('en-US', { hour12: false }),
        type: Math.random() > 0.5 ? 'upload' : 'alert',
        description: Math.random() > 0.5
          ? 'User uploaded new media file (Trust: 0.91)'
          : 'Risk alert: Inappropriate content detected',
        riskLevel: Math.random() > 0.7 ? 'high' : 'medium',
        countryCode: ['US', 'GB', 'DE', 'JP', 'CN'][Math.floor(Math.random() * 5)],
      };
      setActivityFeed(prev => [newEvent, ...prev.slice(0, 20)]);
    }
  };
  // Prefixed with underscore - declared but not currently used
  // Can be enabled when table sorting is implemented
  const _handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };
  const sortedCreatorTools = [...MOCK_CREATOR_TOOLS].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'rank':
        comparison = a.rank - b.rank;
        break;
      case 'uploadCount':
        comparison = a.uploadCount - b.uploadCount;
        break;
      case 'avgTrust':
        comparison = a.avgTrust - b.avgTrust;
        break;
      case 'riskPercent':
        comparison = a.riskPercent - b.riskPercent;
        break;
      default:
        comparison = 0;
    }
    return sortOrder === 'asc' ? comparison : -comparison;
  });
  const handleRefresh = () => {
    updateStats();
    const btn = document.getElementById('refresh-btn');
    if (btn) {
      btn.classList.add('animate-spin');
      setTimeout(() => btn.classList.remove('animate-spin'), 1000);
    }
  };
  // ───────────────────────────────────────────────────────────────────
  // RENDER - License Modal
  // ───────────────────────────────────────────────────────────────────
  if (showLicenseModal) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              License Required
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              This feature requires a Business or Enterprise subscription plan.
            </p>
          </div>
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Current Plan:</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white capitalize">Free</p>
            </div>
            <button
              onClick={() => navigate('/billing')}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              <TrendingUp className="w-5 h-5" />
              Upgrade to Business
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading telemetry data...</p>
        </div>
      </div>
    );
  }
  // ───────────────────────────────────────────────────────────────────
  // RENDER - Main Telemetry Dashboard
  // ───────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Custom Scrollbar Styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-track {
          background: #374151;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #6b7280;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #a1a1a1;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Real-Time Analytics Dashboard</h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm">Live intelligence & telemetry monitoring</p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-100 dark:bg-emerald-900/30 border border-emerald-300 dark:border-emerald-700 rounded-lg">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            <span className="text-emerald-700 dark:text-emerald-400 text-sm font-medium">Live</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 px-4 py-2 bg-emerald-100 dark:bg-emerald-900/30 border border-emerald-300 dark:border-emerald-700 rounded-lg">
            <Shield className="w-5 h-5 text-emerald-700 dark:text-emerald-400" />
            <span className="text-emerald-700 dark:text-emerald-400 text-sm font-medium">Admin Access</span>
          </div>
          <button
            onClick={() => navigate('/admin/analytics')}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-sm font-medium">Exit Admin</span>
          </button>
        </div>
      </div>
      {/* Top Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {/* Active Users */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-gray-600 dark:text-gray-400 text-sm">Active Users</p>
            <Users className="w-4 h-4 text-gray-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{formatNumber(stats.activeUsers)}</p>
          <div className="flex items-center gap-2 mt-2">
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            <span className="text-emerald-600 dark:text-emerald-400 text-xs">Live</span>
            <span className="text-gray-500 text-xs">(+/-)</span>
          </div>
          <div className="mt-2 h-8">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={MOCK_UPLOAD_PATTERNS.slice(-5)}>
                <Line type="monotone" dataKey="weekday" stroke="#10b981" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* Requests/Min */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-gray-600 dark:text-gray-400 text-sm">Requests/Min</p>
            <Zap className="w-4 h-4 text-gray-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.requestsPerMin}</p>
          <div className="mt-2 h-12">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={MOCK_CATEGORY_TREND.slice(-5)}>
                <defs>
                  <linearGradient id="colorRequests" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <Area type="monotone" dataKey="value" stroke="#10b981" fillOpacity={1} fill="url(#colorRequests)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <p className="text-gray-500 dark:text-gray-500 text-xs mt-1">Real time</p>
        </div>
        {/* Avg Processing Time */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-gray-600 dark:text-gray-400 text-sm">Avg Processing Time</p>
            <Clock className="w-4 h-4 text-gray-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.avgProcessingTime.toFixed(1)}s</p>
          <div className="mt-2 h-8">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={MOCK_ERROR_RATE.slice(-5)}>
                <Line type="monotone" dataKey="rate" stroke="#3b82f6" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-gray-500 dark:text-gray-500 text-xs mt-1">Live</p>
        </div>
        {/* Risk Alerts */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-gray-600 dark:text-gray-400 text-sm">Risk Alerts (Last Hour)</p>
            <AlertTriangle className="w-4 h-4 text-gray-500" />
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.riskAlerts}</p>
          <div className="flex items-center gap-2 mt-2">
            <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs rounded">{stats.riskAlerts}</span>
            <span className="text-gray-500 dark:text-gray-500 text-xs">Inappropriate content detected</span>
          </div>
        </div>
      </div>
      {/* Main Tabbed Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 mb-6">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav className="flex gap-6">
            {[
              { id: 'map', label: 'Live Map', icon: Globe },
              { id: 'content', label: 'Content Origin Telemetry', icon: Activity },
              { id: 'behavior', label: 'User Behavior & Trends', icon: TrendingUp },
              { id: 'system', label: 'System Health & Performance', icon: Server },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as ActiveTab)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
        {/* Tab Content */}
        <div>
          {/* TAB 1: LIVE MAP */}
          {activeTab === 'map' && (
            <div>
              {/* Map Controls - Docked */}
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-gray-900 dark:text-white text-sm font-medium">Live</span>
                  </div>
                  <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                    <button
                      onClick={() => setRegionFilter('global')}
                      className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                        regionFilter === 'global'
                          ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      Global
                    </button>
                    <button
                      onClick={() => setRegionFilter('region')}
                      className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                        regionFilter === 'region'
                          ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      Region
                    </button>
                  </div>
                  <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
                    {(['5m', '15m', '1h', '24h'] as TimeRange[]).map((range) => (
                      <button
                        key={range}
                        onClick={() => setTimeRange(range)}
                        className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                          timeRange === range
                            ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                        }`}
                      >
                        {range}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  id="refresh-btn"
                  onClick={handleRefresh}
                  className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-xs font-medium transition-colors"
                >
                  <RefreshCw className="w-3 h-3" />
                  Refresh
                </button>
              </div>
              {/* Map Visualization - FIXED */}
              <div className="relative h-96 bg-gray-200 dark:bg-gray-900 rounded-lg overflow-hidden border border-gray-300 dark:border-gray-700">
                {/* World map background with proper styling */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <Globe className="w-full h-full text-gray-400 dark:text-gray-600 opacity-20" />
                </div>
                {/* Data points */}
                {geoData.slice(0, 8).map((geo, idx) => (
                  <div
                    key={idx}
                    className="absolute transform -translate-x-1/2 -translate-y-1/2 group cursor-pointer"
                    style={{
                      left: `${((geo.coordinates.lng + 180) / 360) * 100}%`,
                      top: `${((90 - geo.coordinates.lat) / 180) * 100}%`,
                    }}
                  >
                    <div
                      className={`rounded-full ${getRiskBgColor(geo.riskLevel)} opacity-70 animate-pulse hover:opacity-100 hover:scale-125 transition-all`}
                      style={{
                        width: `${Math.min(24, 8 + geo.uploads / 25)}px`,
                        height: `${Math.min(24, 8 + geo.uploads / 25)}px`
                      }}
                      title={`${geo.country}: ${geo.uploads} uploads`}
                    />
                    {/* Tooltip on hover */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10 shadow-lg">
                      <p className="font-semibold">{geo.country}</p>
                      <p>Uploads: {geo.uploads}</p>
                      <p>Trust: {(geo.avgTrustScore * 100).toFixed(0)}%</p>
                      <p className={`capitalize ${getRiskColor(geo.riskLevel)}`}>Risk: {geo.riskLevel}</p>
                    </div>
                  </div>
                ))}
                {/* Legend */}
                <div className="absolute bottom-4 left-4 bg-white dark:bg-gray-800/90 rounded-lg p-3 border border-gray-200 dark:border-gray-700 shadow-lg">
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 font-medium">Country Stats</p>
                  <div className="space-y-1 text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-500" />
                      <span className="text-gray-700 dark:text-gray-300">Uploads</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-500" />
                      <span className="text-gray-700 dark:text-gray-300">Top Tools</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-red-500" />
                      <span className="text-gray-700 dark:text-gray-300">Risk Distribution</span>
                    </div>
                  </div>
                </div>
                {/* Bubble Size Legend */}
                <div className="absolute bottom-4 right-4 bg-white dark:bg-gray-800/90 rounded-lg p-3 border border-gray-200 dark:border-gray-700 shadow-lg">
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 font-medium">Bubble Size</p>
                  <div className="space-y-1 text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-gradient-to-r from-emerald-500 to-red-500" />
                      <span className="text-gray-700 dark:text-gray-300">Ave. Trust Score</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-gray-400 dark:bg-gray-600" />
                      <span className="text-gray-700 dark:text-gray-300">Low → High Risk</span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Map Attribution */}
              <div className="mt-3 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <div className="flex items-center gap-4">
                  <span>mapbox</span>
                  <select className="bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-gray-700 dark:text-gray-300">
                    <option>Region Filter</option>
                    <option>North America</option>
                    <option>Europe</option>
                    <option>Asia</option>
                  </select>
                </div>
                <button className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                  <div className="flex gap-0.5">
                    <div className="w-1 h-3 bg-blue-500 rounded" />
                    <div className="w-1 h-3 bg-blue-500 rounded" />
                  </div>
                </button>
              </div>
            </div>
          )}
          {/* TAB 2: CONTENT ORIGIN TELEMETRY (With Sub-Tabs) */}
          {activeTab === 'content' && (
            <div>
              {/* Sub-Tab Navigation */}
              <div className="flex gap-4 mb-6 border-b border-gray-200 dark:border-gray-700 pb-2">
                <button
                  onClick={() => setContentSubTab('tools')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    contentSubTab === 'tools'
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <BarChart3 className="w-4 h-4" />
                  Creator Tools Leaderboard
                </button>
                <button
                  onClick={() => setContentSubTab('distribution')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    contentSubTab === 'distribution'
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <PieChartIcon className="w-4 h-4" />
                  Content Type Distribution
                </button>
              </div>
              {/* Creator Tools Leaderboard */}
              {contentSubTab === 'tools' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Creator Tools Leaderboard</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Rank</th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Tool/Platform</th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Upload Count</th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Avg Trust</th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Risk %</th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-gray-600 dark:text-gray-400">Trend</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {sortedCreatorTools.map((tool) => {
                          const Icon = tool.icon || Box;
                          return (
                            <tr key={tool.rank} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                              <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">{tool.rank}</td>
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                                    <Icon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                  </div>
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">{tool.tool}</span>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-gray-900 dark:text-white">{formatNumber(tool.uploadCount)}</span>
                                  <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-blue-500 rounded-full"
                                      style={{ width: `${Math.min(100, (tool.uploadCount / 3300) * 100)}%` }}
                                    />
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2">
                                  <div className="w-12 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-emerald-500 rounded-full"
                                      style={{ width: `${tool.avgTrust * 100}%` }}
                                    />
                                  </div>
                                  <span className="text-sm text-emerald-600 dark:text-emerald-400">{tool.avgTrust.toFixed(2)}</span>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <span className={`text-sm font-medium ${
                                  tool.riskPercent < 20 ? 'text-emerald-600 dark:text-emerald-400' :
                                  tool.riskPercent < 50 ? 'text-yellow-600 dark:text-yellow-400' :
                                  'text-red-600 dark:text-red-400'
                                }`}>
                                  {tool.riskPercent}%
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <div className="w-16 h-6">
                                  <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={tool.trend.map((v, i) => ({ i, v }))}>
                                      <Line type="monotone" dataKey="v" stroke="#10b981" strokeWidth={2} dot={false} />
                                    </LineChart>
                                  </ResponsiveContainer>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {/* Content Type Distribution */}
              {contentSubTab === 'distribution' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Content Type Distribution</h3>
                    <div className="flex items-center gap-6 mb-6">
                      <div className="w-48 h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={MOCK_CONTENT_TYPES}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={90}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {MOCK_CONTENT_TYPES.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                            </Pie>
                            <Tooltip
                              contentStyle={{ backgroundColor: '#1f2937', borderRadius: '8px', border: '1px solid #374151', color: '#fff' }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex-1 grid grid-cols-2 gap-2 text-xs">
                        {MOCK_CONTENT_TYPES.map((type, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: type.color }} />
                            <span className="text-gray-700 dark:text-gray-300">{type.name}</span>
                            <span className="text-gray-500 dark:text-gray-400">{type.value}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  {/* Category Trend */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Category Trend over Last 24h</h3>
                    <div className="h-64 mb-6">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={MOCK_CATEGORY_TREND}>
                          <defs>
                            <linearGradient id="colorTrend" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="time" stroke="#6b7280" fontSize={12} />
                          <YAxis stroke="#6b7280" fontSize={12} />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#1f2937', borderRadius: '8px', border: '1px solid #374151', color: '#fff' }}
                          />
                          <Area type="monotone" dataKey="value" stroke="#3b82f6" fillOpacity={1} fill="url(#colorTrend)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* TAB 3: USER BEHAVIOR & TRENDS (With Sub-Tabs) */}
          {activeTab === 'behavior' && (
            <div>
              {/* Sub-Tab Navigation */}
              <div className="flex gap-4 mb-6 border-b border-gray-200 dark:border-gray-700 pb-2">
                <button
                  onClick={() => setBehaviorSubTab('patterns')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    behaviorSubTab === 'patterns'
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <BarChart3 className="w-4 h-4" />
                  Upload Patterns
                </button>
                <button
                  onClick={() => setBehaviorSubTab('tags')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    behaviorSubTab === 'tags'
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <Tag className="w-4 h-4" />
                  Tag Adoption & Crowdsourcing
                </button>
              </div>
              {/* Upload Patterns */}
              {behaviorSubTab === 'patterns' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Upload Patterns Heatmap</h4>
                    {/* Heatmap */}
                    <div className="mb-4">
                      <div className="grid grid-cols-12 gap-1 mb-2">
                        {['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb'].map((month, idx) => (
                          <div key={idx} className="text-xs text-gray-500 dark:text-gray-400 text-center">{month}</div>
                        ))}
                      </div>
                      <div className="grid grid-cols-12 gap-1">
                        {[...Array(48)].map((_, idx) => (
                          <div
                            key={idx}
                            className={`h-4 rounded-sm ${
                              Math.random() > 0.5 ? 'bg-emerald-500' : 'bg-emerald-900'
                            }`}
                            style={{ opacity: 0.3 + Math.random() * 0.7 }}
                          />
                        ))}
                      </div>
                      <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <span>Real</span>
                        <span>UTC</span>
                      </div>
                    </div>
                    {/* Line Chart */}
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={MOCK_UPLOAD_PATTERNS}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="hour" stroke="#6b7280" fontSize={12} />
                          <YAxis stroke="#6b7280" fontSize={12} />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#1f2937', borderRadius: '8px', border: '1px solid #374151', color: '#fff' }}
                          />
                          <Legend />
                          <Line type="monotone" dataKey="weekday" stroke="#3b82f6" strokeWidth={2} dot={false} name="Weekday" />
                          <Line type="monotone" dataKey="weekend" stroke="#10b981" strokeWidth={2} dot={false} name="Weekend" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    {/* Insights */}
                    <div className="mt-4 space-y-2">
                      <div className="flex items-center justify-between text-xs p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <span className="text-gray-600 dark:text-gray-400">Insights</span>
                        <span className="text-gray-900 dark:text-white">Peak upload time: 2-4 PM UTC</span>
                      </div>
                      <div className="flex items-center justify-between text-xs p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <span className="text-gray-600 dark:text-gray-400">Insights</span>
                        <span className="text-emerald-600 dark:text-emerald-400">Weekend uploads +34%</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-center">
                    <div className="text-center text-gray-500 dark:text-gray-400">
                      <Upload className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p>Additional upload analytics coming soon</p>
                    </div>
                  </div>
                </div>
              )}
              {/* Tag Adoption & Crowdsourcing */}
              {behaviorSubTab === 'tags' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Tag Adoption</h4>
                    <div className="space-y-2 max-h-96 overflow-y-auto custom-scrollbar pr-2">
                      {MOCK_TAG_ADOPTION.map((tagData, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{tagData.tag}</p>
                            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                              <span>Count: {tagData.count}</span>
                              <span className={tagData.growthRate >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
                                {tagData.growthRate >= 0 ? '+' : ''}{tagData.growthRate}%
                              </span>
                            </div>
                          </div>
                          <button className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors">
                            Suggest Category
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Crowdsourcing Info</h4>
                    <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">For admins: Map user tags to official categories for better content classification.</p>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Confidence Score</span>
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 bg-gray-300 dark:bg-gray-600 rounded-full overflow-hidden">
                              <div className="w-3/4 h-full bg-emerald-500" />
                            </div>
                            <span className="text-emerald-600 dark:text-emerald-400 font-medium">75%</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <h5 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">Tag Guidelines</h5>
                      <ul className="text-xs text-blue-800 dark:text-blue-400 space-y-1">
                        <li>• Use specific, descriptive tags</li>
                        <li>• Avoid duplicate or similar tags</li>
                        <li>• Map to official categories when possible</li>
                        <li>• Review suggested categories regularly</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* TAB 4: SYSTEM HEALTH & PERFORMANCE (Flipped Layout) */}
          {activeTab === 'system' && (
            <div className="space-y-6">
              {/* Queue Depth, Error Rate, Resource Usage - NOW AT TOP */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Queue Depth */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Queue Depth</h4>
                  <div className="h-24 mb-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={MOCK_QUEUE_DEPTH}>
                        <defs>
                          <linearGradient id="colorQueue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="time" stroke="#6b7280" fontSize={10} />
                        <YAxis stroke="#6b7280" fontSize={10} />
                        <Area type="monotone" dataKey="depth" stroke="#3b82f6" fillOpacity={1} fill="url(#colorQueue)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">12</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Current queue size</p>
                </div>
                {/* Error Rate */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Error Rate</h4>
                  <div className="h-24 mb-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={MOCK_ERROR_RATE}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="time" stroke="#6b7280" fontSize={10} />
                        <YAxis stroke="#6b7280" fontSize={10} />
                        <Line type="monotone" dataKey="rate" stroke="#ef4444" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">1.8%</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Last 6 hours average</p>
                </div>
                {/* Resource Usage with Dials - FIXED FOR LIGHT MODE */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Resource Usage</h4>
                  <div className="flex justify-around">
                    <Gauge
                      value={45}
                      label="CPU"
                      color="#3b82f6"
                      size="sm"
                    />
                    <Gauge
                      value={62}
                      label="Memory"
                      color="#8b5cf6"
                      size="sm"
                    />
                    <Gauge
                      value={38}
                      label="Disk"
                      color="#10b981"
                      size="sm"
                    />
                  </div>
                </div>
              </div>
              {/* Provider Status with Dials - NOW AT BOTTOM */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Provider Status</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {MOCK_PROVIDER_STATUS.map((provider) => (
                    <div key={provider.name} className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-gray-900 dark:text-white">{provider.name}</h4>
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                          provider.status === 'operational'
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                            : provider.status === 'degraded'
                            ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                          {provider.status}
                        </span>
                      </div>
                      <div className="flex justify-center mb-4">
                        <Gauge
                          value={provider.successRate}
                          label="Success Rate"
                          color={provider.successRate >= 95 ? '#10b981' : provider.successRate >= 80 ? '#f59e0b' : '#ef4444'}
                          size="md"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600 dark:text-gray-400 text-xs">Response Time</p>
                          <p className="font-semibold text-gray-900 dark:text-white">{provider.responseTime}ms</p>
                        </div>
                        <div>
                          <p className="text-gray-600 dark:text-gray-400 text-xs">Errors</p>
                          <p className="font-semibold text-gray-900 dark:text-white">{provider.errors}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Live Activity Feed - Unattached Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Live Activity Feed</h3>
          <button
            onClick={() => setFeedEnabled(!feedEnabled)}
            className={`w-12 h-6 rounded-full transition-colors ${
              feedEnabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <div className={`w-5 h-5 bg-white rounded-full shadow-md transform transition-transform ${
              feedEnabled ? 'translate-x-6' : 'translate-x-0.5'
            }`} />
          </button>
        </div>
        {/* Filters */}
        <div className="flex items-center gap-2 mb-3 text-xs">
          <select
            value={eventFilter}
            onChange={(e) => setEventFilter(e.target.value)}
            className="bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-gray-700 dark:text-gray-300"
          >
            <option value="all">Event type</option>
            <option value="upload">Upload</option>
            <option value="alert">Alert</option>
            <option value="tag">Tag</option>
          </select>
          <select
            value={riskFilter}
            onChange={(e) => setRiskFilter(e.target.value)}
            className="bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-gray-700 dark:text-gray-300"
          >
            <option value="all">Risk level</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
          <select className="bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-gray-700 dark:text-gray-300">
            <option>Region</option>
            <option>Content type</option>
          </select>
        </div>
        {/* Activity List */}
        <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar pr-2">
          {activityFeed.slice(0, 8).map((event) => (
            <div key={event.id} className="flex items-start gap-2 text-xs p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <span className="text-gray-500 dark:text-gray-400">[{event.timestamp}]</span>
              {event.countryCode && (
                <span className="text-lg" title={event.countryCode}>
                  {getCountryFlag(event.countryCode)}
                </span>
              )}
              <span className={
                event.type === 'alert' ? 'text-red-600 dark:text-red-400' :
                event.type === 'milestone' ? 'text-emerald-600 dark:text-emerald-400' :
                'text-gray-900 dark:text-gray-300'
              }>
                {event.description}
              </span>
              {event.type === 'upload' && <CheckCircle className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />}
              {event.type === 'alert' && <AlertTriangle className="w-3 h-3 text-yellow-600 dark:text-yellow-400" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
export default IntelTelemetry;
